// src/components/ui/Modal/Modal.tsx
import React, {useState, useEffect, useCallback, useRef} from 'react';
import {IonIcon} from '@ionic/react';
import {closeOutline, chevronBackOutline, chevronDownOutline, chevronForwardOutline, copyOutline, checkmarkCircleOutline} from 'ionicons/icons';

// ═══════════════════════════════════════════════════════════════════
// TYPES & INTERFACES
// ═══════════════════════════════════════════════════════════════════

/** Modal size presets */
export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

/** Modal visual variant */
export type ModalVariant = 'default' | 'sheet' | 'centered';

/** Header configuration */
export interface ModalHeaderProps {
    /** Title text */
    title?: string;
    /** Subtitle/description */
    subtitle?: string;
    /** Show back button instead of close */
    showBack?: boolean;
    /** Back button handler */
    onBack?: () => void;
    /** Custom left slot content */
    leftSlot?: React.ReactNode;
    /** Custom right slot content */
    rightSlot?: React.ReactNode;
    /** Hide the default close/back button */
    hideClose?: boolean;
    /** Sticky header */
    sticky?: boolean;
    /** Center the title */
    centerTitle?: boolean;
    /** Custom background style */
    background?: 'default' | 'transparent' | 'accent';
}

/** Footer configuration */
export interface ModalFooterProps {
    /** Primary action button */
    primaryAction?: {
        label: string;
        onClick: () => void;
        loading?: boolean;
        disabled?: boolean;
        variant?: 'primary' | 'cta' | 'success' | 'danger';
    };
    /** Secondary action button */
    secondaryAction?: {
        label: string;
        onClick: () => void;
        disabled?: boolean;
        variant?: 'default' | 'danger';
    };
    /** Tertiary/destructive action button (shown on the left) */
    tertiaryAction?: {
        label: string;
        onClick: () => void;
        disabled?: boolean;
        variant?: 'default' | 'danger';
    };
    /** Status text shown on the left (e.g., "Saving...", "Saved") */
    status?: string;
    /** Custom footer content (replaces default actions) */
    children?: React.ReactNode;
    /** Sticky footer */
    sticky?: boolean;
    /** Footer alignment */
    align?: 'start' | 'center' | 'end' | 'space-between';
    /** Show divider line */
    showDivider?: boolean;
}

/** Main Modal props */
export interface ModalProps {
    /** Whether modal is open */
    isOpen: boolean;
    /** Close handler */
    onClose: () => void;
    /** Modal size */
    size?: ModalSize;
    /** Modal variant */
    variant?: ModalVariant;
    /** Header configuration */
    header?: ModalHeaderProps;
    /** Footer configuration */
    footer?: ModalFooterProps;
    /** Modal content */
    children: React.ReactNode;
    /** Disable backdrop click to close */
    disableBackdropDismiss?: boolean;
    /** Disable escape key to close */
    disableEscapeDismiss?: boolean;
    /** Custom className for content wrapper */
    contentClassName?: string;
    /** Show loading overlay */
    loading?: boolean;
    /** Z-index override */
    zIndex?: number;
    /** Animation on open */
    animation?: 'fade' | 'slide-up' | 'scale';
    /** Padding for content area */
    contentPadding?: 'none' | 'sm' | 'md' | 'lg';
    /** Aria label for accessibility */
    ariaLabel?: string;
    /** Called after modal opens */
    onDidOpen?: () => void;
    /** Called after modal closes */
    onDidClose?: () => void;
}

// ═══════════════════════════════════════════════════════════════════
// STYLE CONSTANTS
// ═══════════════════════════════════════════════════════════════════

const SIZE_CONFIG: Record<ModalSize, {
    width: string;
    maxWidth: string;
    maxHeight: string;
    borderRadius: number;
}> = {
    sm: {width: '100%', maxWidth: '360px', maxHeight: '70vh', borderRadius: 16},
    md: {width: '100%', maxWidth: '480px', maxHeight: '80vh', borderRadius: 16},
    lg: {width: '100%', maxWidth: '640px', maxHeight: '85vh', borderRadius: 16},
    xl: {width: '100%', maxWidth: '800px', maxHeight: '90vh', borderRadius: 16},
    full: {width: '100%', maxWidth: '100%', maxHeight: '100%', borderRadius: 0},
};

const PADDING_CONFIG: Record<NonNullable<ModalProps['contentPadding']>, string> = {
    none: '0',
    sm: '12px',
    md: '16px',
    lg: '24px',
};

const VARIANT_CONFIG: Record<ModalVariant, {
    position: string;
    transform: string;
    mobilePosition?: string;
}> = {
    default: {
        position: 'center',
        transform: 'translate(-50%, -50%)',
    },
    sheet: {
        position: 'bottom',
        transform: 'translateX(-50%)',
        mobilePosition: 'bottom',
    },
    centered: {
        position: 'center',
        transform: 'translate(-50%, -50%)',
    },
};

// ═══════════════════════════════════════════════════════════════════
// MODAL HEADER COMPONENT
// ═══════════════════════════════════════════════════════════════════

interface InternalHeaderProps extends ModalHeaderProps {
    onClose: () => void;
}

const ModalHeader: React.FC<InternalHeaderProps> = ({
    title,
    subtitle,
    showBack = false,
    onBack,
    leftSlot,
    rightSlot,
    hideClose = false,
    sticky = true,
    centerTitle = false,
    background = 'default',
    onClose,
}) => {
    const [isScrolled, setIsScrolled] = useState(false);

    const bgStyles: Record<typeof background, React.CSSProperties> = {
        default: {
            background: 'var(--ion-card-background)',
        },
        transparent: {
            background: 'transparent',
        },
        accent: {
            background: 'var(--ion-color-step-50)',
        },
    };

    // Close button component
    const CloseButton = () => (
        <button
            onClick={(e) => {
                e.stopPropagation();
                onClose();
            }}
            aria-label="Close modal"
            style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                border: 'none',
                background: 'var(--ion-color-step-50)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.15s ease',
            }}
            onMouseEnter={e => {
                e.currentTarget.style.background = 'var(--ion-color-step-100)';
            }}
            onMouseLeave={e => {
                e.currentTarget.style.background = 'var(--ion-color-step-50)';
            }}
        >
            <IonIcon
                icon={closeOutline}
                style={{fontSize: 20, color: 'var(--ion-text-color)'}}
            />
        </button>
    );

    // Back button component
    const BackButton = () => (
        <button
            onClick={(e) => {
                e.stopPropagation();
                onBack?.();
            }}
            aria-label="Go back"
            style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                border: 'none',
                background: 'var(--ion-color-step-50)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.15s ease',
            }}
            onMouseEnter={e => {
                e.currentTarget.style.background = 'var(--ion-color-step-100)';
            }}
            onMouseLeave={e => {
                e.currentTarget.style.background = 'var(--ion-color-step-50)';
            }}
        >
            <IonIcon
                icon={chevronBackOutline}
                style={{fontSize: 20, color: 'var(--ion-text-color)'}}
            />
        </button>
    );

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '16px 16px 12px',
                position: sticky ? 'sticky' : 'relative',
                top: 0,
                zIndex: 10,
                ...bgStyles[background],
                boxShadow: isScrolled ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                transition: 'box-shadow 0.2s ease',
            }}
        >
            {/* Left slot - back button or custom content */}
            <div style={{display: 'flex', alignItems: 'center'}}>
                {leftSlot || (showBack && <BackButton />)}
            </div>

            {/* Title area */}
            <div style={{
                flex: 1,
                textAlign: centerTitle ? 'center' : 'left',
                minWidth: 0,
            }}>
                {title && (
                    <h2 style={{
                        fontFamily: 'var(--kff-heading-font)',
                        fontSize: 18,
                        fontWeight: 600,
                        color: 'var(--ion-text-color)',
                        margin: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}>
                        {title}
                    </h2>
                )}
                {subtitle && (
                    <p style={{
                        fontFamily: 'var(--kff-data-font)',
                        fontSize: 13,
                        color: 'var(--ion-color-medium)',
                        margin: '2px 0 0',
                    }}>
                        {subtitle}
                    </p>
                )}
            </div>

            {/* Right slot - close button or custom content */}
            <div style={{display: 'flex', alignItems: 'center', minWidth: 40, justifyContent: 'flex-end'}}>
                {rightSlot || (!hideClose && <CloseButton />)}
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════
// MODAL FOOTER COMPONENT
// ═══════════════════════════════════════════════════════════════════

const ModalFooter: React.FC<ModalFooterProps> = ({
    primaryAction,
    secondaryAction,
    tertiaryAction,
    status,
    children,
    sticky = true,
    align = 'end',
    showDivider = true,
}) => {
    const [primaryHovered, setPrimaryHovered] = useState(false);
    const [secondaryHovered, setSecondaryHovered] = useState(false);
    const [tertiaryHovered, setTertiaryHovered] = useState(false);

    const alignStyles: Record<typeof align, string> = {
        start: 'flex-start',
        center: 'center',
        end: 'flex-end',
        'space-between': 'space-between',
    };

    const getVariantStyles = (variant: string, isHovered: boolean): React.CSSProperties => {
        const base: React.CSSProperties = {
            padding: '12px 24px',
            borderRadius: 10,
            border: 'none',
            cursor: 'pointer',
            fontFamily: 'var(--kff-heading-font)',
            fontSize: 15,
            fontWeight: 600,
            transition: 'all 0.2s ease',
        };

        switch (variant) {
            case 'cta':
                return {
                    ...base,
                    background: 'var(--kff-forge-gold)',
                    color: '#0a1a2a',
                    transform: isHovered ? 'translateY(-1px)' : 'none',
                    boxShadow: isHovered ? '0 6px 20px rgba(203, 160, 82, 0.45)' : 'none',
                };
            case 'success':
                return {
                    ...base,
                    background: 'var(--kff-timberline-green)',
                    color: '#fff',
                    transform: isHovered ? 'translateY(-1px)' : 'none',
                    boxShadow: isHovered ? '0 6px 20px rgba(46, 90, 54, 0.4)' : 'none',
                };
            case 'danger':
                return {
                    ...base,
                    background: 'var(--ion-color-danger)',
                    color: '#fff',
                    transform: isHovered ? 'translateY(-1px)' : 'none',
                    boxShadow: isHovered ? '0 6px 20px rgba(220, 38, 38, 0.4)' : 'none',
                };
            case 'primary':
            default:
                return {
                    ...base,
                    background: 'var(--kff-foundry-blue)',
                    color: '#fff',
                    transform: isHovered ? 'translateY(-1px)' : 'none',
                    boxShadow: isHovered ? '0 6px 20px rgba(27, 59, 90, 0.4)' : 'none',
                };
        }
    };

    // If children are passed, render them without wrapper styling (children handle their own layout)
    if (children) {
        return (
            <div style={{
                position: sticky ? 'sticky' : 'relative',
                bottom: 0,
                zIndex: 10,
            }}>
                {children}
            </div>
        );
    }

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: (status || tertiaryAction) ? 'space-between' : alignStyles[align],
                gap: 12,
                padding: '12px 16px',
                position: sticky ? 'sticky' : 'relative',
                bottom: 0,
                background: 'var(--ion-card-background)',
                borderTop: showDivider ? '1px solid var(--ion-border-color)' : 'none',
                zIndex: 10,
            }}
        >
            {/* Left side: status and tertiary action */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {status && (
                    <span style={{
                        fontFamily: 'var(--kff-data-font)',
                        fontSize: 13,
                        color: 'var(--ion-color-medium)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                    }}>
                        {status === 'Saving...' && (
                            <span className="modal-spinner" style={{ width: 14, height: 14 }} />
                        )}
                        {status === 'Saved' && (
                            <span className="material-symbols-outlined" style={{ fontSize: 16, color: 'var(--kff-timberline-green)' }}>
                                check_circle
                            </span>
                        )}
                        {status}
                    </span>
                )}
                {tertiaryAction && (
                    <button
                        onClick={tertiaryAction.onClick}
                        disabled={tertiaryAction.disabled}
                        onMouseEnter={() => setTertiaryHovered(true)}
                        onMouseLeave={() => setTertiaryHovered(false)}
                        style={{
                            padding: '10px 16px',
                            borderRadius: 8,
                            border: tertiaryAction.variant === 'danger'
                                ? '1px solid var(--ion-color-danger)'
                                : '1px solid var(--ion-border-color)',
                            background: tertiaryAction.variant === 'danger'
                                ? (tertiaryHovered ? 'var(--ion-color-danger)' : 'transparent')
                                : (tertiaryHovered ? 'var(--ion-color-step-50)' : 'transparent'),
                            cursor: tertiaryAction.disabled ? 'not-allowed' : 'pointer',
                            fontFamily: 'var(--kff-heading-font)',
                            fontSize: 14,
                            fontWeight: 500,
                            color: tertiaryAction.variant === 'danger'
                                ? (tertiaryHovered ? '#fff' : 'var(--ion-color-danger)')
                                : 'var(--ion-text-color)',
                            opacity: tertiaryAction.disabled ? 0.5 : 1,
                            transition: 'all 0.15s ease',
                        }}
                    >
                        {tertiaryAction.label}
                    </button>
                )}
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {secondaryAction && (
                <button
                    onClick={secondaryAction.onClick}
                    disabled={secondaryAction.disabled}
                    onMouseEnter={() => setSecondaryHovered(true)}
                    onMouseLeave={() => setSecondaryHovered(false)}
                    style={{
                        padding: '12px 24px',
                        borderRadius: 10,
                        border: secondaryAction.variant === 'danger'
                            ? '1px solid var(--ion-color-danger)'
                            : '1px solid var(--ion-border-color)',
                        background: secondaryAction.variant === 'danger'
                            ? (secondaryHovered ? 'var(--ion-color-danger)' : 'var(--ion-color-danger-tint)')
                            : (secondaryHovered ? 'var(--ion-color-step-50)' : 'transparent'),
                        cursor: secondaryAction.disabled ? 'not-allowed' : 'pointer',
                        fontFamily: 'var(--kff-heading-font)',
                        fontSize: 15,
                        fontWeight: 600,
                        color: secondaryAction.variant === 'danger'
                            ? (secondaryHovered ? '#fff' : 'var(--ion-color-danger-shade)')
                            : 'var(--ion-text-color)',
                        opacity: secondaryAction.disabled ? 0.5 : 1,
                        transition: 'all 0.15s ease',
                    }}
                >
                    {secondaryAction.label}
                </button>
            )}
            {primaryAction && (
                <button
                    onClick={primaryAction.onClick}
                    disabled={primaryAction.disabled || primaryAction.loading}
                    onMouseEnter={() => setPrimaryHovered(true)}
                    onMouseLeave={() => setPrimaryHovered(false)}
                    style={{
                        ...getVariantStyles(primaryAction.variant || 'primary', primaryHovered),
                        opacity: primaryAction.disabled || primaryAction.loading ? 0.6 : 1,
                        cursor: primaryAction.disabled || primaryAction.loading ? 'not-allowed' : 'pointer',
                    }}
                >
                    {primaryAction.loading ? (
                        <span style={{display: 'flex', alignItems: 'center', gap: 8}}>
                            <span className="modal-spinner" />
                            Loading...
                        </span>
                    ) : primaryAction.label}
                </button>
            )}
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════
// LOADING OVERLAY
// ═══════════════════════════════════════════════════════════════════

const LoadingOverlay: React.FC = () => (
    <div style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(var(--ion-background-color-rgb), 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        backdropFilter: 'blur(2px)',
    }}>
        <div style={{
            width: 48,
            height: 48,
            border: '3px solid var(--ion-color-step-100)',
            borderTopColor: 'var(--kff-forge-gold)',
            borderRadius: '50%',
            animation: 'modal-spin 0.8s linear infinite',
        }} />
    </div>
);

// ═══════════════════════════════════════════════════════════════════
// MAIN MODAL COMPONENT
// ═══════════════════════════════════════════════════════════════════

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    size = 'md',
    variant = 'default',
    header,
    footer,
    children,
    disableBackdropDismiss = false,
    disableEscapeDismiss = false,
    contentClassName,
    loading = false,
    zIndex = 1000,
    animation = 'scale',
    contentPadding = 'md',
    ariaLabel,
    onDidOpen,
    onDidClose,
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const modalRef = useRef<HTMLDivElement>(null);
    const previousActiveElement = useRef<Element | null>(null);

    const sizeConfig = SIZE_CONFIG[size];

    // Handle open/close animations
    useEffect(() => {
        if (isOpen) {
            previousActiveElement.current = document.activeElement;
            setIsVisible(true);
            setIsAnimating(true);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setIsAnimating(false);
                    onDidOpen?.();
                });
            });
        } else if (isVisible) {
            setIsAnimating(true);
            const timer = setTimeout(() => {
                setIsVisible(false);
                setIsAnimating(false);
                onDidClose?.();
                // Restore focus
                if (previousActiveElement.current instanceof HTMLElement) {
                    previousActiveElement.current.focus();
                }
            }, 200);
            return () => clearTimeout(timer);
        }
    }, [isOpen]);

    // Handle escape key
    useEffect(() => {
        if (!isOpen || disableEscapeDismiss) return;

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, disableEscapeDismiss, onClose]);

    // Lock body scroll when open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Focus trap
    useEffect(() => {
        if (!isOpen || !modalRef.current) return;

        const focusableElements = modalRef.current.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

        const handleTab = (e: KeyboardEvent) => {
            if (e.key !== 'Tab') return;

            if (e.shiftKey) {
                if (document.activeElement === firstElement) {
                    e.preventDefault();
                    lastElement?.focus();
                }
            } else {
                if (document.activeElement === lastElement) {
                    e.preventDefault();
                    firstElement?.focus();
                }
            }
        };

        document.addEventListener('keydown', handleTab);
        firstElement?.focus();

        return () => document.removeEventListener('keydown', handleTab);
    }, [isOpen, isVisible]);

    const handleBackdropClick = useCallback((e: React.MouseEvent) => {
        if (e.target === e.currentTarget && !disableBackdropDismiss) {
            onClose();
        }
    }, [disableBackdropDismiss, onClose]);

    // Animation styles
    const getAnimationStyles = (): React.CSSProperties => {
        const isClosing = !isOpen && isAnimating;
        const isOpening = isOpen && isAnimating;

        const baseTransform = variant === 'sheet'
            ? 'translateX(-50%)'
            : 'translate(-50%, -50%)';

        switch (animation) {
            case 'slide-up':
                return {
                    transform: isOpening || isClosing
                        ? `${baseTransform} translateY(${isClosing ? '20px' : '20px'})`
                        : baseTransform,
                    opacity: isOpening || isClosing ? 0 : 1,
                };
            case 'fade':
                return {
                    transform: baseTransform,
                    opacity: isOpening || isClosing ? 0 : 1,
                };
            case 'scale':
            default:
                return {
                    transform: isOpening || isClosing
                        ? `${baseTransform} scale(${isClosing ? 0.95 : 0.95})`
                        : baseTransform,
                    opacity: isOpening || isClosing ? 0 : 1,
                };
        }
    };

    if (!isVisible) return null;

    return (
        <>
            {/* Keyframe animations */}
            <style>{`
                @keyframes modal-spin {
                    to { transform: rotate(360deg); }
                }
                @keyframes modal-backdrop-in {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
            `}</style>

            {/* Backdrop */}
            <div
                onClick={handleBackdropClick}
                style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    backdropFilter: 'blur(4px)',
                    zIndex,
                    opacity: isOpen && !isAnimating ? 1 : 0,
                    transition: 'opacity 0.2s ease',
                }}
                aria-hidden="true"
            />

            {/* Modal container */}
            <div
                ref={modalRef}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabel || header?.title}
                onClick={handleBackdropClick}
                style={{
                    position: 'fixed',
                    left: '50%',
                    ...(variant === 'sheet'
                        ? {bottom: 0, transform: 'translateX(-50%)'}
                        : {top: '50%', transform: 'translate(-50%, -50%)'}
                    ),
                    width: sizeConfig.width,
                    maxWidth: sizeConfig.maxWidth,
                    maxHeight: sizeConfig.maxHeight,
                    zIndex: zIndex + 1,
                    display: 'flex',
                    flexDirection: 'column',
                    background: 'var(--ion-card-background)',
                    borderRadius: variant === 'sheet'
                        ? `${sizeConfig.borderRadius}px ${sizeConfig.borderRadius}px 0 0`
                        : sizeConfig.borderRadius,
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    overflow: 'hidden',
                    transition: 'transform 0.2s ease, opacity 0.2s ease',
                    ...getAnimationStyles(),
                }}
            >
                {/* Sheet handle for sheet variant */}
                {variant === 'sheet' && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        padding: '12px 0 0',
                    }}>
                        <div style={{
                            width: 40,
                            height: 4,
                            borderRadius: 2,
                            background: 'var(--ion-color-step-200)',
                        }} />
                    </div>
                )}

                {/* Header */}
                {header && (
                    <div onClick={e => e.stopPropagation()}>
                        <ModalHeader {...header} onClose={onClose} />
                    </div>
                )}

                {/* Content */}
                <div
                    className={contentClassName}
                    style={{
                        flex: 1,
                        overflow: 'auto',
                        padding: PADDING_CONFIG[contentPadding],
                        position: 'relative',
                    }}
                    onClick={e => e.stopPropagation()}
                >
                    {children}
                </div>

                {/* Footer */}
                {footer && <ModalFooter {...footer} />}

                {/* Loading overlay */}
                {loading && <LoadingOverlay />}
            </div>
        </>
    );
};

// ═══════════════════════════════════════════════════════════════════
// COMPOUND COMPONENTS FOR FLEXIBLE CONTENT LAYOUT
// ═══════════════════════════════════════════════════════════════════

/** Hero section for amount/stat display */
export interface ModalHeroProps {
    children: React.ReactNode;
    background?: 'default' | 'accent' | 'gradient';
    padding?: 'sm' | 'md' | 'lg';
}

export const ModalHero: React.FC<ModalHeroProps> = ({
    children,
    background = 'accent',
    padding = 'lg',
}) => {
    const bgStyles: Record<typeof background, React.CSSProperties> = {
        default: {background: 'var(--ion-card-background)'},
        accent: {background: 'var(--ion-color-step-50)'},
        gradient: {
            background: 'linear-gradient(135deg, var(--kff-foundry-blue) 0%, #17334f 100%)',
            color: '#fff',
        },
    };

    const paddingValues = {sm: 16, md: 24, lg: 32};

    return (
        <div style={{
            textAlign: 'center',
            padding: paddingValues[padding],
            ...bgStyles[background],
        }}>
            {children}
        </div>
    );
};

/** Field spacing presets */
export type FieldSpacing = 'none' | 'tight' | 'normal' | 'relaxed';

const fieldSpacingValues: Record<FieldSpacing, number> = {
    none: 0,
    tight: 4,
    normal: 8,
    relaxed: 16,
};

/** Section layout options */
export type ModalSectionLayout = 'column' | 'row' | 'grid';
export type ModalSectionBackground = 'none' | 'subtle' | 'muted' | 'accent';

/** Section with optional header */
export interface ModalSectionProps {
    title?: string;
    children: React.ReactNode;
    divider?: boolean;
    /** Background shading */
    background?: ModalSectionBackground;
    /** Spacing between child elements */
    spacing?: FieldSpacing;
    /** Custom horizontal padding (overrides default 16px) */
    padding?: string;
    /** Top padding in pixels */
    paddingTop?: number;
    /** Bottom padding in pixels */
    paddingBottom?: number;
    /** Layout direction: column (default), row, or grid */
    layout?: ModalSectionLayout;
    /** Number of columns for grid layout (default: 2) */
    columns?: number;
    /** Whether row items should wrap */
    wrap?: boolean;
    /** Align items in row layout */
    alignItems?: 'start' | 'center' | 'end' | 'stretch';
}

const sectionBackgroundColors: Record<ModalSectionBackground, string> = {
    none: 'transparent',
    subtle: 'var(--ion-color-step-50, rgba(0,0,0,0.02))',
    muted: 'var(--ion-color-step-100, rgba(0,0,0,0.05))',
    accent: 'var(--ion-color-primary-tint, var(--ion-color-step-100))',
};

export const ModalSection: React.FC<ModalSectionProps> = ({
    title,
    children,
    divider = true,
    background = 'none',
    spacing = 'normal',
    padding,
    paddingTop,
    paddingBottom,
    layout = 'column',
    columns = 2,
    wrap = true,
    alignItems = 'stretch',
}) => {
    const getLayoutStyles = (): React.CSSProperties => {
        const gap = fieldSpacingValues[spacing];

        switch (layout) {
            case 'row':
                return {
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: wrap ? 'wrap' : 'nowrap',
                    gap,
                    alignItems,
                };
            case 'grid':
                return {
                    display: 'grid',
                    gridTemplateColumns: `repeat(${columns}, 1fr)`,
                    gap,
                    alignItems,
                };
            case 'column':
            default:
                return {
                    display: 'flex',
                    flexDirection: 'column',
                    gap,
                };
        }
    };

    // Calculate default padding values
    const defaultPaddingTop = title ? 0 : 8; // Title has its own top padding
    const defaultPaddingBottom = title ? 16 : 8;

    return (
        <div style={{
            paddingLeft: 16,
            paddingRight: 16,
            paddingTop: paddingTop ?? defaultPaddingTop,
            ...(padding ? { padding } : {}), // Allow full override with padding prop
            borderBottom: divider ? '1px solid var(--ion-border-color)' : 'none',
            backgroundColor: sectionBackgroundColors[background],
        }}>
            {title && (
                <h3 style={{
                    fontFamily: 'var(--kff-data-font)',
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: 'var(--ion-color-medium)',
                    padding: '16px 0 8px',
                    margin: 0,
                }}>
                    {title}
                </h3>
            )}
            <div style={{
                ...getLayoutStyles(),
                paddingBottom: paddingBottom ?? defaultPaddingBottom,
            }}>
                {children}
            </div>
        </div>
    );
};

/** Helper component for controlling item width in row/grid layouts */
export interface ModalSectionItemProps {
    children: React.ReactNode;
    /** Width as percentage or CSS value (e.g., '50%', '200px', 'auto') */
    width?: string;
    /** Flex grow value */
    grow?: number;
    /** Flex shrink value */
    shrink?: number;
    /** Min width */
    minWidth?: string;
}

export const ModalSectionItem: React.FC<ModalSectionItemProps> = ({
    children,
    width,
    grow = 0,
    shrink = 1,
    minWidth,
}) => (
    <div style={{
        width,
        flexGrow: grow,
        flexShrink: shrink,
        minWidth: minWidth || 0,
    }}>
        {children}
    </div>
);
ModalSectionItem.displayName = 'ModalSectionItem';

/** Flex row for horizontal layout within ModalSection */
export interface ModalFlexRowProps {
    children: React.ReactNode;
    /** Gap between items in pixels */
    gap?: number;
    /** Vertical alignment */
    alignItems?: 'start' | 'center' | 'end' | 'stretch' | 'baseline';
    /** Horizontal distribution */
    justifyContent?: 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';
    /** Allow wrapping to next line */
    wrap?: boolean;
}

export const ModalFlexRow: React.FC<ModalFlexRowProps> = ({
    children,
    gap = 12,
    alignItems = 'start',
    justifyContent = 'start',
    wrap = false,
}) => {
    const justifyMap: Record<string, string> = {
        start: 'flex-start',
        center: 'center',
        end: 'flex-end',
        between: 'space-between',
        around: 'space-around',
        evenly: 'space-evenly',
    };

    const alignMap: Record<string, string> = {
        start: 'flex-start',
        center: 'center',
        end: 'flex-end',
        stretch: 'stretch',
        baseline: 'baseline',
    };

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'row',
            gap,
            alignItems: alignMap[alignItems],
            justifyContent: justifyMap[justifyContent],
            flexWrap: wrap ? 'wrap' : 'nowrap',
            width: '100%',
        }}>
            {children}
        </div>
    );
};
ModalFlexRow.displayName = 'ModalFlexRow';

/** Row for inline field display */
export interface ModalRowProps {
    icon?: string;
    iconColor?: string;
    label?: string;
    children: React.ReactNode;
    onClick?: () => void;
    interactive?: boolean;
}

export const ModalRow: React.FC<ModalRowProps> = ({
    icon,
    iconColor,
    label,
    children,
    onClick,
    interactive = false,
}) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <div
            onClick={onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '14px 0',
                borderBottom: '1px solid var(--ion-color-step-100)',
                cursor: onClick || interactive ? 'pointer' : 'default',
                background: isHovered && (onClick || interactive) ? 'var(--ion-color-step-50)' : 'transparent',
                margin: '0 -16px',
                paddingLeft: 16,
                paddingRight: 16,
                transition: 'background 0.15s ease',
            }}
        >
            {icon && (
                <IonIcon
                    icon={icon}
                    style={{
                        fontSize: 20,
                        color: iconColor || 'var(--ion-color-medium)',
                        flexShrink: 0,
                    }}
                />
            )}
            {label && (
                <span style={{
                    fontFamily: 'var(--kff-data-font)',
                    fontSize: 14,
                    color: 'var(--ion-color-medium)',
                    minWidth: 80,
                }}>
                    {label}
                </span>
            )}
            <div style={{flex: 1, minWidth: 0}}>
                {children}
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════
// EDITABLE ROW - Auto-save inline editing
// ═══════════════════════════════════════════════════════════════════

export interface ModalEditableRowProps {
    /** Current value */
    value: string;
    /** Called when value changes (for controlled input) */
    onChange?: (value: string) => void;
    /** Called when edit is complete (blur/enter) - for auto-save */
    onSave?: (value: string) => void;
    /** Icon to display */
    icon?: string;
    /** Icon color */
    iconColor?: string;
    /** Placeholder when empty */
    placeholder?: string;
    /** Text shown when empty and not editing */
    emptyText?: string;
    /** Enable multiline textarea */
    multiline?: boolean;
    /** Font size */
    fontSize?: number;
    /** Font weight */
    fontWeight?: number;
    /** Disabled state */
    disabled?: boolean;
}

export const ModalEditableRow: React.FC<ModalEditableRowProps> = ({
    value,
    onChange,
    onSave,
    icon,
    iconColor = 'var(--ion-color-medium)',
    placeholder = 'Add text...',
    emptyText = 'Not set',
    multiline = false,
    fontSize = 15,
    fontWeight = 500,
    disabled = false,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [localValue, setLocalValue] = useState(value);
    const inputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Sync local value with prop
    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    // Focus input when editing starts
    useEffect(() => {
        if (isEditing) {
            if (multiline) {
                textareaRef.current?.focus();
            } else {
                inputRef.current?.focus();
            }
        }
    }, [isEditing, multiline]);

    // Handle click outside to save
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                handleBlur();
            }
        };

        if (isEditing) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isEditing, localValue, value]);

    const handleBlur = () => {
        setIsEditing(false);
        if (localValue !== value) {
            onChange?.(localValue);
            onSave?.(localValue);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !multiline) {
            e.preventDefault();
            handleBlur();
        }
        if (e.key === 'Escape') {
            setLocalValue(value);
            setIsEditing(false);
        }
    };

    const startEditing = () => {
        if (!disabled) {
            setIsEditing(true);
        }
    };

    const inputStyle: React.CSSProperties = {
        flex: 1,
        background: 'transparent',
        border: 'none',
        outline: 'none',
        fontSize,
        fontWeight,
        color: 'var(--ion-text-color)',
        padding: 0,
        width: '100%',
        fontFamily: 'var(--kff-data-font)',
    };

    return (
        <div
            ref={containerRef}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => !isEditing && startEditing()}
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '14px 16px',
                margin: '0 -16px',
                borderBottom: isHovered || isEditing
                    ? '1px solid var(--ion-color-step-300)'
                    : '1px solid var(--ion-color-step-100)',
                cursor: disabled ? 'not-allowed' : isEditing ? 'text' : 'pointer',
                background: isHovered && !isEditing ? 'var(--ion-color-step-50)' : 'transparent',
                transition: 'all 0.15s ease',
                opacity: disabled ? 0.5 : 1,
            }}
        >
            {icon && (
                <IonIcon
                    icon={icon}
                    style={{
                        fontSize: 20,
                        color: iconColor,
                        flexShrink: 0,
                        marginTop: multiline ? 2 : 0,
                    }}
                />
            )}

            <div style={{flex: 1, minWidth: 0}}>
                {isEditing ? (
                    multiline ? (
                        <textarea
                            ref={textareaRef}
                            value={localValue}
                            onChange={e => setLocalValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={placeholder}
                            rows={3}
                            style={{
                                ...inputStyle,
                                resize: 'vertical',
                                minHeight: 60,
                                lineHeight: 1.5,
                            }}
                        />
                    ) : (
                        <input
                            ref={inputRef}
                            type="text"
                            value={localValue}
                            onChange={e => setLocalValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={placeholder}
                            style={inputStyle}
                        />
                    )
                ) : (
                    <div
                        style={{
                            fontSize,
                            fontWeight: localValue ? fontWeight : 400,
                            fontFamily: 'var(--kff-data-font)',
                            color: localValue ? 'var(--ion-text-color)' : 'var(--ion-color-medium)',
                            lineHeight: 1.5,
                            minHeight: multiline ? 22 : 'auto',
                        }}
                    >
                        {localValue || emptyText}
                    </div>
                )}
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════
// COLLAPSIBLE SECTION - Expandable section for additional actions
// ═══════════════════════════════════════════════════════════════════

export interface ModalCollapsibleSectionProps {
    /** Section title */
    title: string;
    /** Icon for the header */
    icon?: string;
    /** Icon color */
    iconColor?: string;
    /** Whether section is expanded by default */
    defaultExpanded?: boolean;
    /** Controlled expanded state */
    expanded?: boolean;
    /** Callback when expanded state changes */
    onExpandedChange?: (expanded: boolean) => void;
    /** Children content */
    children: React.ReactNode;
}

export const ModalCollapsibleSection: React.FC<ModalCollapsibleSectionProps> = ({
    title,
    icon,
    iconColor = 'var(--ion-color-medium)',
    defaultExpanded = false,
    expanded: controlledExpanded,
    onExpandedChange,
    children,
}) => {
    const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
    const isControlled = controlledExpanded !== undefined;
    const isExpanded = isControlled ? controlledExpanded : internalExpanded;

    const handleToggle = () => {
        const newValue = !isExpanded;
        if (!isControlled) {
            setInternalExpanded(newValue);
        }
        onExpandedChange?.(newValue);
    };

    return (
        <div style={{
            borderTop: '1px solid var(--ion-border-color)',
        }}>
            {/* Collapsible Header */}
            <button
                onClick={handleToggle}
                style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '14px 16px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'background 0.15s ease',
                }}
                onMouseEnter={e => {
                    e.currentTarget.style.background = 'var(--ion-color-step-50)';
                }}
                onMouseLeave={e => {
                    e.currentTarget.style.background = 'transparent';
                }}
            >
                {icon && (
                    <IonIcon
                        icon={icon}
                        style={{
                            fontSize: 20,
                            color: iconColor,
                        }}
                    />
                )}
                <span style={{
                    flex: 1,
                    textAlign: 'left',
                    fontFamily: 'var(--kff-heading-font)',
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'var(--ion-text-color)',
                }}>
                    {title}
                </span>
                <IonIcon
                    icon={chevronDownOutline}
                    style={{
                        fontSize: 18,
                        color: 'var(--ion-color-medium)',
                        transition: 'transform 0.2s ease',
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                />
            </button>

            {/* Collapsible Content */}
            <div style={{
                overflow: 'hidden',
                maxHeight: isExpanded ? '500px' : '0',
                opacity: isExpanded ? 1 : 0,
                transition: 'max-height 0.3s ease, opacity 0.2s ease',
            }}>
                <div style={{padding: '0 16px 16px'}}>
                    {children}
                </div>
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════
// ACTION ROW - Toggle/checkbox row for collapsible sections
// ═══════════════════════════════════════════════════════════════════

export interface ModalActionRowProps {
    /** Icon for the action */
    icon: string;
    /** Icon color */
    iconColor?: string;
    /** Title text */
    title: string;
    /** Subtitle/description text */
    subtitle?: string;
    /** Whether this is a toggle (vs just clickable) */
    toggle?: boolean;
    /** Toggle checked state */
    checked?: boolean;
    /** Called when toggle changes */
    onToggle?: (checked: boolean) => void;
    /** Called when row is clicked (for non-toggle actions) */
    onClick?: () => void;
    /** Show divider below */
    divider?: boolean;
}

export const ModalActionRow: React.FC<ModalActionRowProps> = ({
    icon,
    iconColor = 'var(--ion-color-medium)',
    title,
    subtitle,
    toggle = false,
    checked = false,
    onToggle,
    onClick,
    divider = true,
}) => {
    const handleClick = () => {
        if (toggle) {
            onToggle?.(!checked);
        } else {
            onClick?.();
        }
    };

    return (
        <button
            onClick={handleClick}
            style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 0',
                background: 'transparent',
                border: 'none',
                borderBottom: divider ? '1px solid var(--ion-border-color)' : 'none',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'background 0.15s ease',
            }}
        >
            {/* Icon */}
            <div style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: `${iconColor}15`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
            }}>
                <IonIcon
                    icon={icon}
                    style={{
                        fontSize: 18,
                        color: iconColor,
                    }}
                />
            </div>

            {/* Text */}
            <div style={{flex: 1, minWidth: 0}}>
                <div style={{
                    fontFamily: 'var(--kff-data-font)',
                    fontSize: 14,
                    fontWeight: 500,
                    color: 'var(--ion-text-color)',
                }}>
                    {title}
                </div>
                {subtitle && (
                    <div style={{
                        fontFamily: 'var(--kff-data-font)',
                        fontSize: 12,
                        color: 'var(--ion-color-medium)',
                        marginTop: 2,
                    }}>
                        {subtitle}
                    </div>
                )}
            </div>

            {/* Toggle or Chevron */}
            {toggle ? (
                <div style={{
                    width: 44,
                    height: 26,
                    borderRadius: 13,
                    background: checked ? 'var(--kff-timberline-green)' : 'var(--ion-color-step-200)',
                    position: 'relative',
                    transition: 'background 0.2s ease',
                    flexShrink: 0,
                }}>
                    <div style={{
                        position: 'absolute',
                        top: 2,
                        left: checked ? 20 : 2,
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        background: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        transition: 'left 0.2s ease',
                    }} />
                </div>
            ) : (
                <IonIcon
                    icon={chevronForwardOutline}
                    style={{
                        fontSize: 18,
                        color: 'var(--ion-color-medium)',
                        flexShrink: 0,
                    }}
                />
            )}
        </button>
    );
};

// ═══════════════════════════════════════════════════════════════════
// INFO FOOTER - Metadata display like TransactionDetailModal
// ═══════════════════════════════════════════════════════════════════

export interface ModalInfoItem {
    /** Icon to display */
    icon: string;
    /** Text label */
    label: string;
    /** Icon color */
    iconColor?: string;
}

export interface ModalInfoFooterProps {
    /** Array of info items to display in the top row */
    items?: ModalInfoItem[];
    /** Copyable text (like original bank description) */
    copyableText?: string;
    /** Called when copy button clicked */
    onCopy?: (text: string) => void;
    /** Custom children instead of items */
    children?: React.ReactNode;
}

export const ModalInfoFooter: React.FC<ModalInfoFooterProps> = ({
    items,
    copyableText,
    onCopy,
    children,
}) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        if (copyableText) {
            navigator.clipboard.writeText(copyableText);
            setCopied(true);
            onCopy?.(copyableText);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <div style={{
            borderTop: '1px solid var(--ion-border-color)',
            background: 'var(--ion-card-background)',
        }}>
            {/* Info items row */}
            {(items && items.length > 0) || children ? (
                <div style={{
                    padding: '12px 16px 8px',
                    fontSize: 12,
                    fontFamily: 'var(--kff-data-font)',
                    color: 'var(--ion-color-medium)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    flexWrap: 'wrap',
                }}>
                    {children || items?.map((item, index) => (
                        <React.Fragment key={index}>
                            {index > 0 && <span style={{color: 'var(--ion-color-step-300)'}}>|</span>}
                            <span style={{display: 'inline-flex', alignItems: 'center', gap: 4}}>
                                <IonIcon
                                    icon={item.icon}
                                    style={{
                                        fontSize: 14,
                                        color: item.iconColor || 'var(--ion-color-medium)',
                                    }}
                                />
                                {item.label}
                            </span>
                        </React.Fragment>
                    ))}
                </div>
            ) : null}

            {/* Copyable text row - centered with copy icon on right */}
            {copyableText && (
                <div
                    onClick={handleCopy}
                    style={{
                        padding: '4px 16px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        cursor: 'pointer',
                        transition: 'opacity 0.15s ease',
                    }}
                    onMouseEnter={e => {
                        e.currentTarget.style.opacity = '0.8';
                    }}
                    onMouseLeave={e => {
                        e.currentTarget.style.opacity = '1';
                    }}
                >
                    <div style={{
                        flex: 1,
                        fontSize: 12,
                        fontFamily: 'var(--kff-data-font)',
                        fontStyle: 'italic',
                        color: 'var(--ion-color-step-500)',
                        lineHeight: 1.4,
                        textAlign: 'center',
                    }}>
                        {copyableText}
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 28,
                            height: 28,
                            borderRadius: 6,
                            background: copied ? 'var(--ion-color-success-tint)' : 'var(--ion-color-step-100)',
                            color: copied ? 'var(--ion-color-success)' : 'var(--ion-color-step-500)',
                            transition: 'all 0.15s ease',
                            flexShrink: 0,
                        }}
                    >
                        <IonIcon
                            icon={copied ? checkmarkCircleOutline : copyOutline}
                            style={{fontSize: 14}}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

Modal.displayName = 'Modal';
ModalHero.displayName = 'ModalHero';
ModalSection.displayName = 'ModalSection';
ModalRow.displayName = 'ModalRow';
ModalEditableRow.displayName = 'ModalEditableRow';
ModalCollapsibleSection.displayName = 'ModalCollapsibleSection';
ModalActionRow.displayName = 'ModalActionRow';
ModalInfoFooter.displayName = 'ModalInfoFooter';

export default Modal;
